import {
  OFFLINE_QUEUE_STORAGE_KEY,
  activeCaseCode,
  activeUserId,
  dispatchScopedStorageUpdate,
  filterItemsForCase,
  itemCaseCode,
  itemUserId,
  readJson,
  withCaseScope,
  writeJson,
} from "./case-scoped-storage";

const generateId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
const PROCESSORS = new Map();

const loadAllQueue = () => readJson(OFFLINE_QUEUE_STORAGE_KEY, []);

const loadQueue = (caseCode, userId = activeUserId()) => {
  if (!caseCode) return [];
  return filterItemsForCase(loadAllQueue(), caseCode, userId);
};

const saveQueue = (queue, caseCode = activeCaseCode(), userId = activeUserId()) => {
  writeJson(OFFLINE_QUEUE_STORAGE_KEY, queue);
  dispatchScopedStorageUpdate(
    "offline-queue:updated",
    OFFLINE_QUEUE_STORAGE_KEY,
    caseCode,
    userId
  );
};

const upsertQueueItem = (queue, item) => {
  if (!item) return queue;
  if (item.dedupeKey) {
    const itemScope = itemCaseCode(item);
    const userScope = itemUserId(item);
    const existingIndex = queue.findIndex(
      (entry) =>
        entry.dedupeKey === item.dedupeKey &&
        (!itemScope || itemCaseCode(entry) === itemScope) &&
        (!userScope || itemUserId(entry) === userScope)
    );
    if (existingIndex >= 0) {
      queue[existingIndex] = {
        ...queue[existingIndex],
        ...item,
        status: item.status || queue[existingIndex].status || "pending",
        updatedAt: new Date().toISOString(),
      };
      return queue;
    }
  }

  return [item, ...queue];
};

const retryQueue = async () => {
  const caseCode = activeCaseCode();
  if (!caseCode) return;

  const userId = activeUserId();
  let queue = loadQueue(caseCode, userId);

  for (const item of queue) {
    if (item.status === "retrying" || item.status === "sent") continue;
    if (item.path && item.path !== window.location.pathname) continue;

    const processor = PROCESSORS.get(item.type);
    if (!processor) continue;

    item.status = "retrying";
    item.updatedAt = new Date().toISOString();
    const retryingQueue = loadAllQueue().map((entry) =>
      entry.id === item.id ? item : entry
    );
    saveQueue(retryingQueue, caseCode, userId);

    try {
      await processor(item);
      const remainingQueue = loadAllQueue().filter((entry) => entry.id !== item.id);
      saveQueue(remainingQueue, caseCode, userId);
      queue = loadQueue(caseCode, userId);
    } catch (_error) {
      const updatedQueue = loadAllQueue().map((entry) =>
        entry.id === item.id
          ? { ...entry, status: "failed", updatedAt: new Date().toISOString() }
          : entry
      );
      saveQueue(updatedQueue, caseCode, userId);
      queue = loadQueue(caseCode, userId);
    }
  }
};

if (!window.isxOfflineQueue) {
  window.isxOfflineQueue = {
    add(item) {
      const caseCode = activeCaseCode();
      const userId = activeUserId();
      const queue = loadAllQueue();
      const payload = {
        ...withCaseScope(item, caseCode, userId),
        id: item.id || generateId(),
        status: item.status || "pending",
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      saveQueue(upsertQueueItem(queue, payload), caseCode, userId);
      return payload.id;
    },
    remove(id) {
      const queue = loadAllQueue().filter((entry) => entry.id !== id);
      saveQueue(queue);
    },
    list() {
      const caseCode = activeCaseCode();
      if (!caseCode) return [];
      return loadQueue(caseCode, activeUserId());
    },
    registerProcessor(type, handler) {
      if (!type || typeof handler !== "function") return;
      PROCESSORS.set(type, handler);
      retryQueue();
    },
    retryAll() {
      return retryQueue();
    },
  };
}

const statusLabel = (status) => {
  switch (status) {
    case "retrying":
      return "Retrying";
    case "failed":
      return "Needs retry";
    case "sent":
      return "Sent";
    default:
      return "Queued";
  }
};

const statusClass = (status) => {
  switch (status) {
    case "retrying":
      return "text-yellow-200";
    case "failed":
      return "text-red-200";
    case "sent":
      return "text-green-200";
    default:
      return "text-isx-ltgray";
  }
};

const setBannerOffset = (offset) => {
  document.documentElement.style.setProperty("--offline-banner-offset", offset);
};

const OfflineQueueHook = {
  mounted() {
    this.banner = this.el.querySelector("[data-offline-banner]");
    this.stateLabel = this.el.querySelector("[data-offline-state]");
    this.queueList = this.el.querySelector("[data-offline-queue-list]");
    this.queueCount = this.el.querySelector("[data-offline-queue-count]");
    this.retryButton = this.el.querySelector("[data-offline-retry]");

    this.handleOnline = () => {
      this.isOnline = true;
      this.update();
      window.isxOfflineQueue.retryAll();
    };

    this.handleOffline = () => {
      this.isOnline = false;
      this.update();
    };

    this.handleQueueUpdated = (event) => {
      this.queue = event.detail || [];
      this.update();
    };

    this.retryButton?.addEventListener("click", () => {
      window.isxOfflineQueue.retryAll();
      this.update();
    });

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("offline-queue:updated", this.handleQueueUpdated);

    this.isOnline = navigator.onLine;
    this.queue = window.isxOfflineQueue.list();
    setBannerOffset("0px");
    this.update();
  },
  destroyed() {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("offline-queue:updated", this.handleQueueUpdated);
    document.documentElement.style.removeProperty("--offline-banner-offset");
  },
  update() {
    const queue = window.isxOfflineQueue.list();
    const hasQueue = queue.length > 0;

    if (this.queueCount) {
      this.queueCount.textContent = queue.length.toString();
    }

    if (this.stateLabel) {
      if (!this.isOnline) {
        this.stateLabel.textContent = "Offline";
      } else if (hasQueue) {
        this.stateLabel.textContent = "Reconnecting";
      } else {
        this.stateLabel.textContent = "Online";
      }
    }

    if (this.queueList) {
      this.queueList.innerHTML = "";
      queue.forEach((item) => {
        const li = document.createElement("li");
        li.className = "flex items-center justify-between gap-2 text-xs";

        const label = document.createElement("span");
        label.className = "text-isx-white/90";
        label.textContent = item.label || "Queued action";

        const status = document.createElement("span");
        status.className = statusClass(item.status);
        status.textContent = statusLabel(item.status);

        li.append(label, status);
        this.queueList.appendChild(li);
      });
    }

    if (this.banner) {
      if (!this.isOnline || hasQueue) {
        this.banner.classList.remove("hidden");
      } else {
        this.banner.classList.add("hidden");
      }
    }

    window.requestAnimationFrame(() => {
      const offset =
        this.banner && !this.banner.classList.contains("hidden")
          ? `${this.banner.offsetHeight}px`
          : "0px";

      setBannerOffset(offset);
    });
  },
};

export default OfflineQueueHook;
